import React from 'react';
import { useUser } from '../contexts/UserContext';
import './CreditsBadge.css';

/**
 * Small "credits left" chip rendered on each feature page so the user
 * always knows how many free uses they have for THIS specific feature.
 *
 * Props:
 *   feature  — one of the keys in UserContext.featureCredits
 *              (e.g. 'interviewAssistant', 'mockInterview', 'coverLetter')
 *   label    — short feature name shown in the chip (e.g. "Interview Assistant")
 *   className — optional, lets the parent page position the chip
 *
 * If the user has an active paid subscription we render an "Unlimited"
 * chip instead of a number so they don't see a misleading "999999".
 */
export default function CreditsBadge({ feature, label, className = '' }) {
  const { user, featureCredits, hasActiveSubscription } = useUser();

  // Don't render for signed-out visitors — the credit balance is meaningless
  // and the chip would just say "0 credits".
  if (!user) return null;

  if (hasActiveSubscription) {
    return (
      <div className={`credits-badge credits-badge--unlimited ${className}`} role="status">
        <span className="credits-badge-icon" aria-hidden="true">∞</span>
        <span className="credits-badge-text">
          Unlimited{label ? ` ${label}` : ''} credits
        </span>
      </div>
    );
  }

  const remaining = Number(featureCredits?.[feature] ?? 0);
  const tone = remaining === 0 ? 'empty' : remaining <= 1 ? 'low' : 'normal';

  return (
    <div
      className={`credits-badge credits-badge--${tone} ${className}`}
      role="status"
      title={remaining === 0
        ? 'You\'ve used all your free credits for this feature. Upgrade to Pro for unlimited access.'
        : `You have ${remaining} free credit${remaining === 1 ? '' : 's'} left for this feature.`}
    >
      <span className="credits-badge-dot" aria-hidden="true" />
      <span className="credits-badge-text">
        <strong>{remaining}</strong>
        {' '}credit{remaining === 1 ? '' : 's'} left
        {label ? ` · ${label}` : ''}
      </span>
    </div>
  );
}
